// Command card: the 3x4 grid of buttons at the bottom right.
//
// This module is PURE. It takes the world, the local player and the selected
// entities and returns a plain description of twelve slots. It never touches
// the DOM, never mutates the simulation, and can therefore be unit-tested
// without a browser -- which is the only way the "greyed for the right reason"
// rule can be verified at all.
//
//   commandCard(world, player, ents, mode) -> { mode, slots: Array(12) }
//
// SLOT SHAPE
//   { slot, id, label, short, hotkey, cost, action, enabled, reason, desc }
//     action  { type, defId?, targeted }   what runAction() in hud.js executes
//     reason  null when available; otherwise the EXACT string the simulation
//             would have refused with -- it comes from unavailableReason() in
//             src/sim/commands.js, so the tooltip can never drift from the rule.
//
// WHY THE REASON MATTERS: a button that is simply dark tells a player nothing.
// "Requires Barracks" tells them what to build next; "Additional supply
// required" tells them why their army stopped growing. Every greyed button in
// this card carries the sentence the simulation itself would have produced.

import { UnitDefs, BuildingDefs, ResearchDefs } from '../sim/defs.js';
import { unavailableReason } from '../sim/commands.js';

export const CARD_ROWS = 3;
export const CARD_COLS = 4;
export const CARD_SLOTS = CARD_ROWS * CARD_COLS;

/** Structures offered by the Build submenu, in the order they appear. */
export const BUILD_MENU = ['command_center', 'depot', 'refinery', 'barracks', 'factory', 'starport', 'turret'];

/** Max production queue length; mirrors the check in commands.js. */
export const QUEUE_LIMIT = 5;

/**
 * A building's production queue, tolerating an entity that has none.
 *
 * The card is now also the KEY TABLE (see resolveKey in ./selection.js), which
 * means it is built against whatever entities the caller has -- including the
 * plain object literals the pure tests use. A missing `queue` must read as
 * "empty", not throw, or one malformed entity would take the whole keyboard
 * down with it.
 */
const EMPTY = [];
const queueOf = (b) => (b && Array.isArray(b.queue) ? b.queue : EMPTY);

const isWorkerDef = (defId) => !!(UnitDefs[defId] && UnitDefs[defId].isWorker);
export const isWorker = (e) => e && e.kind === 'unit' && isWorkerDef(e.defId);

/**
 * Fixed hotkeys for the standing orders. These never move between cards --
 * muscle memory is the entire point of a hotkey, and a key that means "Stop"
 * on one selection and "Siege" on another is worse than no key at all.
 */
export const BASIC_KEYS = {
  move: 'M', stop: 'S', attack: 'A', patrol: 'P', hold: 'H',
  gather: 'G', repair: 'R', buildmenu: 'B', rally: 'Y',
  siege: 'O', unsiege: 'O', cloak: 'C', decloak: 'C', cancel: 'Escape',
};

function mk(slot, o) {
  const reason = o.reason || null;
  return {
    slot,
    id: o.id,
    label: o.label,
    short: o.short || o.label,
    hotkey: o.hotkey || '',
    cost: o.cost || null,
    action: o.action,
    enabled: !reason,
    reason,
    desc: o.desc || '',
    kind: o.kind || 'basic',
  };
}

const basic = (slot, id, label, desc, targeted, extra = {}) => mk(slot, {
  id, label, hotkey: BASIC_KEYS[id], desc,
  action: { type: id, targeted },
  ...extra,
});

// ---------------------------------------------------------------------------
// unit cards
// ---------------------------------------------------------------------------

/**
 * One card serves every unit selection. Abilities appear when ANY selected unit
 * has them and the command layer filters out the units that do not -- selecting
 * tanks plus riflemen and pressing Siege must siege the tanks, not refuse.
 */
function unitCard(world, player, units, slots) {
  const anyWorker = units.some(isWorker);
  const tanks = units.filter((u) => UnitDefs[u.defId] && UnitDefs[u.defId].canSiege);
  const cloakers = units.filter((u) => UnitDefs[u.defId] && UnitDefs[u.defId].canCloak);
  const pl = world.players[player];

  slots[0] = basic(0, 'move', 'Move', 'Move to a point. Ignores enemies on the way.', true);
  slots[1] = basic(1, 'stop', 'Stop', 'Cancel all orders and hold position.', false);
  slots[2] = basic(2, 'attack', 'Attack', 'Attack-move: engage anything encountered on the way.', true);
  slots[3] = basic(3, 'patrol', 'Patrol', 'Walk back and forth, attacking what comes into range.', true);
  slots[4] = basic(4, 'hold', 'Hold', 'Hold position: fire without chasing.', false);

  if (anyWorker) {
    slots[5] = basic(5, 'gather', 'Gather', 'Harvest a mineral field or refinery.', true);
    slots[6] = basic(6, 'repair', 'Repair', 'Repair a damaged structure or mechanical unit.', true);
    slots[7] = basic(7, 'buildmenu', 'Build', 'Open the structure list.', false, { kind: 'menu' });
  }

  if (tanks.length) {
    const sieged = tanks.every((t) => t.sieged);
    const id = sieged ? 'unsiege' : 'siege';
    const has = pl.tech.has('siege_tech');
    slots[8] = mk(8, {
      id,
      label: sieged ? 'Unsiege' : 'Siege',
      hotkey: BASIC_KEYS[id],
      desc: sieged
        ? 'Return to tank mode: mobile, 7 range, no splash.'
        : 'Siege mode: immobile, 12 range, splash that also hurts your own units.',
      action: { type: id, targeted: false },
      reason: has ? null : 'Requires Siege Tech',
      kind: 'ability',
    });
  }

  if (cloakers.length) {
    const cloaked = cloakers.every((c) => c.cloaked);
    const id = cloaked ? 'decloak' : 'cloak';
    const has = pl.tech.has('cloaking_field');
    slots[9] = mk(9, {
      id,
      label: cloaked ? 'Decloak' : 'Cloak',
      hotkey: BASIC_KEYS[id],
      desc: cloaked ? 'Become visible again.' : 'Invisible to anything without a detector.',
      action: { type: id, targeted: false },
      reason: has ? null : 'Requires Cloaking Field',
      kind: 'ability',
    });
  }

  return { mode: 'root', slots };
}

// ---------------------------------------------------------------------------
// build submenu
// ---------------------------------------------------------------------------

function buildMenu(world, player, slots) {
  BUILD_MENU.forEach((defId, i) => {
    const def = BuildingDefs[defId];
    if (!def || i >= CARD_SLOTS - 1) return;
    slots[i] = mk(i, {
      id: `build_${defId}`,
      label: def.name,
      short: def.short,
      hotkey: def.hotkey,
      cost: def.cost,
      desc: def.desc,
      action: { type: 'build', defId, targeted: true },
      reason: unavailableReason(world, player, defId),
      kind: 'build',
    });
  });
  slots[CARD_SLOTS - 1] = mk(CARD_SLOTS - 1, {
    id: 'cancelmenu',
    label: 'Cancel',
    hotkey: 'Escape',
    desc: 'Back to the unit commands.',
    action: { type: 'cancelmenu', targeted: false },
    kind: 'menu',
  });
  return { mode: 'build', slots };
}

// ---------------------------------------------------------------------------
// building cards
// ---------------------------------------------------------------------------

/**
 * Reason a train button is unavailable. unavailableReason() answers tech, money
 * and supply; the queue limit lives in applyCommand and has to be mirrored here
 * or a full Barracks would show a lit button that silently does nothing.
 */
function trainReason(world, player, defId, producers) {
  const r = unavailableReason(world, player, defId);
  if (r) return r;
  if (producers.length && producers.every((b) => queueOf(b).length >= QUEUE_LIMIT)) return 'Queue is full';
  return null;
}

function researchReason(world, player, defId, producers) {
  const pl = world.players[player];
  if (pl.tech.has(defId)) return 'Already researched';
  if (producers.some((b) => queueOf(b).some((q) => q.kind === 'research' && q.defId === defId))) return 'In progress';
  if (producers.length && producers.every((b) => queueOf(b).some((q) => q.kind === 'research'))) return 'In progress';
  return unavailableReason(world, player, defId);
}

function buildingCard(world, player, builds, slots) {
  // The card follows the FIRST selected structure. Selecting three Barracks
  // gives one card whose buttons address all three; selecting a Barracks and a
  // Factory shows the Barracks and leaves the Factory alone, exactly like BW.
  const primary = builds[0];
  const def = BuildingDefs[primary.defId];
  const same = builds.filter((b) => b.defId === primary.defId);
  const ready = same.filter((b) => !b.constructing);

  if (primary.constructing) {
    slots[CARD_SLOTS - 1] = mk(CARD_SLOTS - 1, {
      id: 'cancel',
      label: 'Cancel',
      hotkey: 'Escape',
      desc: 'Halt construction and refund the cost.',
      action: { type: 'cancel', targeted: false },
      kind: 'menu',
    });
    return { mode: 'root', slots };
  }

  let i = 0;
  for (const unitId of def.produces || []) {
    const u = UnitDefs[unitId];
    if (!u) continue;
    slots[i] = mk(i, {
      id: `train_${unitId}`,
      label: u.name,
      short: u.short,
      hotkey: u.hotkey,
      cost: u.cost,
      desc: u.desc,
      action: { type: 'train', defId: unitId, targeted: false },
      reason: trainReason(world, player, unitId, ready),
      kind: 'unit',
    });
    i++;
  }
  for (const rid of def.research || []) {
    const r = ResearchDefs[rid];
    if (!r) continue;
    // Hotkeys inside one card must be unique or the second one is unreachable.
    // Siege Tank and Siege Tech both want 'S'; the unit keeps it and the
    // research falls back to the next word's initial ('T'), deterministically.
    const used = new Set(slots.filter(Boolean).map((s) => s.hotkey));
    slots[i] = mk(i, {
      id: `research_${rid}`,
      label: r.name,
      short: r.name,
      hotkey: pickKey(r.hotkey, r.name, used),
      cost: r.cost,
      desc: r.desc,
      action: { type: 'research', defId: rid, targeted: false },
      reason: researchReason(world, player, rid, ready),
      kind: 'research',
    });
    i++;
  }

  if ((def.produces || []).length) {
    slots[8] = mk(8, {
      id: 'rally',
      label: 'Set Rally',
      hotkey: BASIC_KEYS.rally,
      desc: 'Where new units walk when they pop.',
      action: { type: 'rally', targeted: true },
      kind: 'basic',
    });
  }
  if (ready.some((b) => queueOf(b).length)) {
    slots[CARD_SLOTS - 1] = mk(CARD_SLOTS - 1, {
      id: 'cancel',
      label: 'Cancel',
      hotkey: 'Escape',
      desc: 'Cancel the last item in the queue and refund it.',
      action: { type: 'cancel', targeted: false },
      kind: 'menu',
    });
  }
  return { mode: 'root', slots };
}

/** First unused key: the def's own, then each word's initial, then any letter. */
export function pickKey(preferred, name, used) {
  if (preferred && !used.has(preferred)) return preferred;
  const words = String(name).toUpperCase().split(/[^A-Z]+/).filter(Boolean);
  for (const w of words) if (!used.has(w[0])) return w[0];
  for (const ch of String(name).toUpperCase()) if (/[A-Z]/.test(ch) && !used.has(ch)) return ch;
  return '';
}

// ---------------------------------------------------------------------------

/**
 * @param {World} world
 * @param {number} player   the LOCAL player; enemy selections get an empty card
 * @param {Array}  ents     selected entities, in the input layer's stable order
 * @param {string} mode     'root' | 'build'
 */
export function commandCard(world, player, ents, mode = 'root') {
  const slots = new Array(CARD_SLOTS).fill(null);
  const own = (ents || []).filter((e) => e && e.alive && e.owner === player);
  const units = own.filter((e) => e.kind === 'unit');
  const builds = own.filter((e) => e.kind === 'building');

  if (!own.length) return { mode: 'root', slots };
  if (units.length) {
    if (mode === 'build' && units.some(isWorker)) return buildMenu(world, player, slots);
    return unitCard(world, player, units, slots);
  }
  return buildingCard(world, player, builds, slots);
}

/** The button a key press maps to, or null. Case-insensitive for letters. */
export function findByHotkey(card, key) {
  if (!card || !key) return null;
  const k = key.length === 1 ? key.toUpperCase() : key;
  for (const s of card.slots) if (s && s.hotkey === k) return s;
  return null;
}

/** "50" / "150/100" -- the label under a button. */
export function costLabel(cost) {
  if (!cost) return '';
  const m = cost.minerals || 0, g = cost.gas || 0;
  return g ? `${m}/${g}` : `${m}`;
}

/**
 * Which entities a card action actually addresses. The command layer filters
 * again, but the HUD needs this to grey a button that no selected unit can obey.
 */
export function targetsOf(action, ents, player) {
  const own = ents.filter((e) => e && e.alive && e.owner === player);
  switch (action.type) {
    case 'train': case 'research': case 'rally': case 'cancel':
      return own.filter((e) => e.kind === 'building');
    case 'siege': case 'unsiege':
      return own.filter((e) => e.kind === 'unit' && UnitDefs[e.defId] && UnitDefs[e.defId].canSiege);
    case 'cloak': case 'decloak':
      return own.filter((e) => e.kind === 'unit' && UnitDefs[e.defId] && UnitDefs[e.defId].canCloak);
    case 'gather': case 'repair': case 'build': case 'buildmenu':
      return own.filter(isWorker);
    default:
      return own.filter((e) => e.kind === 'unit');
  }
}
